import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useBasicSettingsData } from "api/basic-settings";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useSettings } from "api/general";
import { FormButtons } from "components/FormControls/FormButtons";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { HelpPanel } from "components/HelpPanel/HelpPanel";
import { InfoBox } from "components/Layout/InfoBox";
import { ActiveMemberSection } from "components/Layout/Member/MembersConfiguration/ActiveMemberSection";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { ExternalLink } from "ExternalLink";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { GeneralSettingsModel, RowData } from "types/settings";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { DraggableTableRow } from "./DraggableTableRow";
import { useStyles } from "./MembersConfiguration.styles";

export function MembersConfiguration() {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { updateSettings } = useSettings();
  const settingsData = useBasicSettingsData("general");
  const [rows, setRows] = useState<RowData[]>([]);

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      const [draggedRow] = newRows.splice(dragIndex, 1);
      newRows.splice(hoverIndex, 0, draggedRow);
      return newRows;
    });
  };

  const handleRequiredChange = (rowId: string) => {
    setRows(
      rows.map((row) => ({
        ...row,
        identificationMethod: row.field === rowId,
        // When a row becomes the identification method, it must be unique
        unique: row.field === rowId ? true : row.unique,
      })),
    );
  };

  const handleEventMatchingChange = (rowId: string, value: boolean) => {
    const updatedRows = rows.map((row) => {
      if (row.field === rowId) {
        return {
          ...row,
          eventMatching: value,
          // When event matching is enabled, the field must be unique
          unique: value ? true : row.unique,
        };
      }
      return row;
    });

    // Sort rows to keep non-event matching rows at the bottom
    const sortedRows = [
      ...updatedRows.filter((row) => row.eventMatching),
      ...updatedRows.filter((row) => !row.eventMatching),
    ];

    setRows(sortedRows);
  };

  const handleUniqueChange = (rowId: string, value: boolean) => {
    setRows(
      rows.map((row) => ({
        ...row,
        unique: row.field === rowId ? value : row.unique,
      })),
    );
  };

  const handleConfirmUniqueChange = async (
    formValues: GeneralSettingsModel,
    formSetSubmitting: (isSubmitting: boolean) => void,
    formSetStatus: (status: Record<string, string>) => void,
  ) => {
    setShowConfirmationModal(false);
    try {
      formSetSubmitting(true);
      formSetStatus({});
      const response = await updateSettings({
        ...formValues,
        customersIdentificationConfigurations: rows.map((row, index) => ({
          priority: index + 1,
          field: row.field,
          identificationMethod: row.identificationMethod,
          unique: row.unique,
          eventMatching: row.eventMatching,
        })),
      });
      if (response?.data?.settings) {
        notify(message({ id: "settingsSaved" }));
        formSetSubmitting(false);
      }
    } catch (exception) {
      formSetSubmitting(false);
      notify(getErrorMessage(exception));
      const errors = backendErrors(getBackendErrors(exception));
      formSetStatus(errors);
      focusError();
    }
  };

  useEffect(() => {
    settingsData.otherValues?.customersIdentificationConfigurations &&
      setRows(settingsData.otherValues.customersIdentificationConfigurations);
  }, [settingsData.otherValues?.customersIdentificationConfigurations]);
  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "members" }),
      path: links.membersList,
    },
  };

  return (
    <>
      <TopHeader
        title={message({ id: "configuration" })}
        clearLink={links.membersList}
        breadcrumbs={breadcrumbs}
      />
      <div className={classes.formContainer}>
        {settingsData.formValues ? (
          <Formik
            enableReinitialize
            initialStatus={{}}
            initialValues={{
              ...settingsData.formValues,
              ...(settingsData.formValues?.activeMember?.transactionInXDays && {
                transactionCondition: "transaction",
              }),
              ...(settingsData.formValues?.activeMember
                ?.customEventsInXDays && {
                customEventCondition: "customEvent",
              }),
              ...{
                customersIdentificationConfigurations:
                  settingsData.otherValues?.customersIdentificationConfigurations?.map(
                    (item) => item.field,
                  ) || [],
              },
            }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              const originalRows =
                settingsData.otherValues
                  ?.customersIdentificationConfigurations || [];
              const uniqueChangedFromTrueToFalse = rows.some((row) => {
                const original = originalRows.find(
                  (r) => r.field === row.field,
                );
                return original?.unique === true && row.unique === false;
              });

              if (uniqueChangedFromTrueToFalse) {
                setShowConfirmationModal(true);
                return;
              }

              try {
                setSubmitting(true);
                setStatus({});
                const response = await updateSettings({
                  ...values,
                  activeMember: {
                    ...values.activeMember,
                    customEventsInXDays: {
                      ...values.activeMember?.customEventsInXDays,
                      ...(values.activeMember?.customEventsInXDays
                        ?.allEvents && {
                        eventTypes: undefined,
                      }),
                      allEvents:
                        values.transactionCondition !== "transaction"
                          ? !!values.activeMember?.customEventsInXDays
                              ?.allEvents
                          : undefined,
                    },
                    ...(values.transactionCondition !== "transaction" && {
                      transactionInXDays: undefined,
                    }),
                    ...(values.customEventCondition !== "customEvent" && {
                      customEventsInXDays: undefined,
                    }),
                    ...((values.customEventCondition !== "customEvent" ||
                      values.transactionCondition !== "transaction") && {
                      operator: undefined,
                    }),
                  },
                  customersIdentificationConfigurations: rows.map(
                    (row, index) => ({
                      priority: index + 1,
                      field: row.field,
                      identificationMethod: row.identificationMethod,
                      unique: row.unique,
                      eventMatching: row.eventMatching,
                    }),
                  ),
                });
                if (response?.data?.settings) {
                  notify(message({ id: "settingsSaved" }));
                }
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              status,
              dirty,
              values,
              setSubmitting,
              setStatus,
              initialValues,
            }) => (
              <Form onSubmit={handleSubmit}>
                <ConfirmationModal
                  open={showConfirmationModal}
                  header={message({
                    id: "areYouSureYouWantToPerformThisChange",
                  })}
                  info={message({ id: "activeMemberSettingsChangeWarning" })}
                  action={() =>
                    handleConfirmUniqueChange(values, setSubmitting, setStatus)
                  }
                  close={() => setShowConfirmationModal(false)}
                  cancelText={message({ id: "cancel" })}
                  submitText={message({ id: "yesSaveChanges" })}
                />
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}
                <Grid container spacing={8}>
                  <Grid item xs={8}>
                    <Typography className={classes.header}>
                      {message({ id: "memberIdentifierConfiguration" })}
                    </Typography>
                    <Typography
                      className={`${classes.subheader} ${classes.breakSpace}`}
                    >
                      {message({ id: "memberIdentifiersConfigurationInfo" })}
                    </Typography>
                    <Box mt={8} />
                    <InfoBox
                      text={message({
                        id: "memberIdentifiersConfigurationWarning",
                      })}
                      type="warning"
                    />
                    <Box mt={8} />
                    <DndProvider backend={HTML5Backend}>
                      <TableContainer
                        component={Paper}
                        className={classes.tableContainer}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.headerCell}>
                                <div>
                                  {message({ id: "memberIdentifiers" })}
                                </div>
                              </TableCell>
                              <TableCell
                                align="center"
                                className={classes.headerCell}
                              >
                                <div>
                                  <span>
                                    {message({ id: "requiredField" })}
                                  </span>
                                  <TooltipWithIcon
                                    isWhite
                                    placement="bottom-start"
                                    className={classes.tooltipIcon}
                                    text={
                                      <ExternalLinkTooltipContent
                                        name="requiredField"
                                        text={message({
                                          id: "requiredFieldInfo",
                                        })}
                                      />
                                    }
                                  />
                                </div>
                              </TableCell>
                              <TableCell
                                align="center"
                                className={classes.headerCell}
                              >
                                <div>
                                  <span>
                                    {message({ id: "eventMatching" })}
                                  </span>
                                  <TooltipWithIcon
                                    isWhite
                                    placement="bottom-start"
                                    className={classes.tooltipIcon}
                                    text={
                                      <ExternalLinkTooltipContent
                                        name="eventMatching"
                                        text={message({
                                          id: "eventMatchingInfo",
                                        })}
                                      />
                                    }
                                  />
                                </div>
                              </TableCell>
                              <TableCell
                                align="center"
                                className={classes.headerCell}
                              >
                                <div>
                                  <span>{message({ id: "uniqueField" })}</span>
                                  <TooltipWithIcon
                                    isWhite
                                    placement="bottom-start"
                                    className={classes.tooltipIcon}
                                    text={
                                      <ExternalLinkTooltipContent
                                        name="uniqueField"
                                        text={message({
                                          id: "uniqueFieldInfo",
                                        })}
                                      />
                                    }
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map((row, index) => (
                              <DraggableTableRow
                                key={row.field}
                                row={row}
                                priority={index}
                                moveRow={moveRow}
                                onRequiredChange={handleRequiredChange}
                                onEventMatchingChange={
                                  handleEventMatchingChange
                                }
                                onUniqueChange={handleUniqueChange}
                                settingsData={
                                  settingsData?.otherValues
                                    ?.customersIdentificationConfigurations
                                }
                                rows={rows}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DndProvider>
                    <Box mt={16} />
                    <Typography className={classes.header}>
                      {message({ id: "activeMemberSettings" })}
                    </Typography>
                    <Typography
                      className={`${classes.subheader} ${classes.breakSpace}`}
                    >
                      {message(
                        {
                          id: "activeMemberSettingsInfo",
                        },
                        {
                          link: (
                            <ExternalLink
                              className={classes.link}
                              path="https://help.openloyalty.io/main-features/dashboard/general-overview#active-members"
                              data-test-id="active-members-link"
                            >
                              {message({ id: "learnMore" })}
                            </ExternalLink>
                          ),
                        },
                      )}
                    </Typography>
                    <Box mt={8} />
                    {Array.isArray(status?.activeMember) && (
                      <span className={`${classes.error} error-anchor`}>
                        {status.activeMember}
                      </span>
                    )}
                    <ActiveMemberSection />
                    {!isSubmitting && (
                      <LeaveSiteGuard
                        warnBeforeRefresh
                        data-test-id="members-config"
                        initialValues={initialValues}
                        currentValues={values}
                        isDirty={dirty}
                      />
                    )}
                    <Box mt={12} />
                    <FormButtons
                      disableSubmit={isSubmitting}
                      onCancelClick={() => navigate(links.membersList)}
                      onSubmitClick={() => handleSubmit()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <HelpPanel
                      title="memberIdentifierConfiguration"
                      description="memberIdentifierConfigurationInfo"
                      infoBoxText="memberIdentifierConfigurationWarning"
                      links={[
                        {
                          label: message({ id: "documentation" }),
                          path: "https://help.openloyalty.io/main-features/members/member-list/configuration",
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        ) : (
          <>
            <Skeleton height={300} width="100%" />
            <Box mt={12} />
            <FormButtons
              disableSubmit
              onCancelClick={() => {}}
              onSubmitClick={() => {}}
            />
          </>
        )}
      </div>
    </>
  );
}
